import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import DOMPurify from 'dompurify';
import Prism from 'prismjs';
import './utils/prism-terraform'; // Instead of prismjs/components/prism-terraform
import 'prismjs/themes/prism-tomorrow.css';
import 'prismjs/components/prism-yaml';
import 'prismjs/components/prism-javascript';
import NewsletterSubscribe from './NewsletterSubscribe';

const blogPosts = [
  
  {
    id: 1,
    title: "Zero Trust Architecture for AWS: A Practical Guide",
    excerpt: "Implementing zero trust principles in AWS using IAM, Service Control Policies, and network segmentation techniques.",
    date: "July 28, 2025",
    readTime: "8 min read",
    tags: ["AWS", "Zero Trust", "IAM"],
    image: "/blog-zero-trust.jpg",
    content: `
      <h2>The Death of Perimeter Security</h2>
      <p>Traditional network perimeters have dissolved in cloud environments. Our analysis of 100+ breaches shows 82% involved lateral movement within supposedly "trusted" zones.</p>
      
      <h2>AWS Zero Trust Implementation Framework</h2>
      <h3>1. Identity-Centric Controls</h3>
      <ul>
        <li><strong>IAM Policies:</strong> Implemented least-privilege using AWS Access Analyzer for policy validation</li>
        <li><strong>Session Duration:</strong> Reduced from default 1-hour to 15-minute temporary credentials</li>
      </ul>
      
      <h3>2. Microsegmentation</h3>
      <pre><code class="language-terraform"># Example SCP denying inter-VPC traffic
aws_organizations_policy "deny_cross_vpc" {
  content = jsonencode({
    Version = "2012-10-17",
    Statement = [{
      Effect = "Deny",
      Action = ["ec2:CreateNetworkInterface"],
      Condition = {
        StringNotEquals = {"ec2:Vpc" = ["vpc-123456"]}
      }
    }]
  })
}</code></pre>

      <h2>Case Study: Fintech Deployment</h2>
      <p>Implemented for payment processing environment:</p>
      <table class="min-w-full border">
        <thead><tr><th class="border px-4 py-2">Metric</th><th class="border px-4 py-2">Before</th><th class="border px-4 py-2">After</th></tr></thead>
        <tbody>
          <tr><td class="border px-4 py-2">IAM Permissions</td><td class="border px-4 py-2">Average 142 per role</td><td class="border px-4 py-2">Average 17 per role</td></tr>
          <tr><td class="border px-4 py-2">MTTD</td><td class="border px-4 py-2">4.2 hours</td><td class="border px-4 py-2">11 minutes</td></tr>
        </tbody>
      </table>
    `
  },
  {
    id: 2,
    title: "Automating SOC2 Compliance with Terraform",
    excerpt: "How we helped a fintech startup achieve SOC2 compliance in 6 weeks using infrastructure-as-code security controls.",
    date: "June 15, 2025",
    readTime: "10 min read",
    tags: ["Compliance", "Terraform", "SOC2"],
    image: "/blog-soc2.jpg",
    content: `
  <h2>The SOC2 Automation Paradox</h2>
  <p>While SOC2 requires documented processes, manual compliance creates audit fatigue. Our solution: Terraform modules that enforce controls while generating evidence.</p>
  
  <h2>Key Automation Components</h2>
  <div class="grid md:grid-cols-2 gap-4 mb-6">
    <div class="bg-blue-50 p-4 rounded-lg">
      <h3 class="font-bold mb-2">1. Control Modules</h3>
      <p>Pre-built modules for common requirements:</p>
      <ul class="list-disc pl-5">
        <li>Encryption-at-rest</li>
        <li>Audit logging</li>
        <li>Access reviews</li>
      </ul>
    </div>
    <div class="bg-blue-50 p-4 rounded-lg">
      <h3 class="font-bold mb-2">2. Evidence Generation</h3>
      <p>Automated outputs include:</p>
      <ul class="list-disc pl-5">
        <li>Configuration snapshots</li>
        <li>Policy diffs</li>
        <li>Compliance scores</li>
      </ul>
    </div>
  </div>

  <h2>Implementation Example</h2>
  <pre><code class="language-terraform">module "soc2_encryption" {
  source  = "hunterfortress/soc2/aws//modules/encryption"
  version = "1.3.0"

  kms_key_spec          = "AWS_KMS"
  rotation_interval     = 90
  auto_approval_config = {
    max_key_age = 365
  }
}</code></pre>

  <h2>Results Achieved</h2>
  <p>For healthcare client preparing for IPO:</p>
  <ul class="list-checkmark pl-5">
    <li>Reduced auditor findings by 73%</li>
    <li>Cut compliance costs from $240k to $38k annually</li>
    <li>Enabled continuous compliance monitoring</li>
  </ul>
`
  },
  {
    id: 3,
    title: "Kubernetes Hardening: Beyond the Basics",
    excerpt: "Advanced security measures for production Kubernetes clusters based on NSA/CISA guidelines.",
    date: "June 15, 2025",
    readTime: "12 min read",
    tags: ["Kubernetes", "NSA", "Hardening"],
    image: "/blog-k8s.jpg",
    content: `
  <h2>Beyond CIS Benchmarks</h2>
  <p>While CIS provides good baselines, we implement NSA/CISA's "Defense in Depth" approach with additional protections:</p>
  
  <h2>Critical Hardening Measures</h2>
  <div class="space-y-4">
    <div class="p-4 border-l-4 border-red-500 bg-red-50">
      <h3 class="font-bold">1. Pod Security</h3>
      <ul class="list-disc pl-5">
        <li>Enforce <code>securityContext</code> with <code>readOnlyRootFilesystem: true</code></li>
        <li>Automate drift detection using OPA/Gatekeeper</li>
      </ul>
    </div>
    
    <div class="p-4 border-l-4 border-blue-500 bg-blue-50">
      <h3 class="font-bold">2. Network Hardening</h3>
      <pre><code class="language-yaml"># NetworkPolicy example
apiVersion: networking.k8s.io/v1
kind: NetworkPolicy
metadata:
  name: default-deny
spec:
  podSelector: {}
  policyTypes:
  - Ingress
  - Egress</code></pre>
    </div>
  </div>

  <h2>Runtime Protection</h2>
  <p>Our recommended stack:</p>
  <ol class="list-decimal pl-5">
    <li><strong>Falco</strong> for runtime detection</li>
    <li><strong>kube-bench</strong> for CIS checks</li>
    <li><strong>Kyverno</strong> for policy enforcement</li>
  </ol>

  <h2>Benchmark Results</h2>
  <p>Before/after hardening financial services cluster:</p>
  <div class="bg-gray-100 p-4 rounded-lg">
    <div class="flex justify-between">
      <span>Attack Surface Reduction:</span>
      <span class="font-bold">68%</span>
    </div>
    <div class="h-2 bg-gray-300 rounded-full mt-1">
      <div class="h-2 bg-green-500 rounded-full" style="width: 32%"></div>
    </div>
  </div>
`
  },
  {
    id: 4,
    title: "Incident Response Automation with AWS Lambda",
    excerpt: "Building serverless detection and response pipelines that trigger on GuardDuty findings.",
    date: "June 15, 2025",
    readTime: "6 min read",
    tags: ["AWS", "Lambda", "SecurityAutomation"],
    image: "/blog-lambda.jpg",
    content: `
  <h2>Why Serverless IR?</h2>
  <p>Traditional incident response fails in serverless environments due to:</p>
  <ul class="list-disc pl-5">
    <li>Ephemeral compute instances</li>
    <li>Distributed attack surfaces</li>
    <li>API-driven infrastructure</li>
  </ul>

  <h2>Architecture</h2>
  <div class="bg-gray-800 text-white p-4 rounded-lg mb-6">
    <pre><code class="language-text">GuardDuty Finding → EventBridge → Lambda → 
  ├─ Containment (Disable IAM Keys) 
  ├─ Investigation (Snapshot Volumes)
  └─ Remediation (Terminate Instance)</code></pre>
  </div>

  <h2>Key Lambda Functions</h2>
  <div class="grid md:grid-cols-3 gap-4 mb-6">
    <div class="border p-3 rounded-lg">
      <h3 class="font-bold">1. Triage</h3>
      <p>Enrich alerts with AWS Config data</p>
    </div>
    <div class="border p-3 rounded-lg">
      <h3 class="font-bold">2. Contain</h3>
      <p>Isolate resources via VPC/SG changes</p>
    </div>
    <div class="border p-3 rounded-lg">
      <h3 class="font-bold">3. Notify</h3>
      <p>Create Slack/Teams tickets</p>
    </div>
  </div>

  <h2>Example Code Snippet</h2>
  <pre><code class="language-javascript">// Disable compromised IAM user
exports.handler = async (event) => {
  const finding = event.detail;
  if (finding.severity > 6) {
    await iam.updateAccessKey({
      UserName: finding.resource.accessKeyDetails.userName,
      AccessKeyId: finding.resource.accessKeyDetails.accessKeyId,
      Status: 'Inactive'
    }).promise();
  }
};</code></pre>

  <h2>Metrics</h2>
  <p>Financial services client results:</p>
  <ul class="list-checkmark pl-5">
    <li>Mean Time to Respond (MTTR): 37s</li>
    <li>False positive rate: 2.1%</li>
    <li>Dwell time reduction: 98%</li>
  </ul>
`
  },
  {
    id: 5,
    title: "Azure Sentinel vs AWS GuardDuty: Enterprise Threat Detection Compared",
    excerpt: "Detailed comparison of cloud-native threat detection capabilities between Azure and AWS security services.",
    date: "March 22, 2025",
    readTime: "9 min read",
    tags: ["Azure", "AWS", "SIEM"],
    image: "/blog-sentinel-vs-guardduty.jpg",
    content: `
      <h2>Detection Capabilities Face-off</h2>
      <p>We evaluated both platforms across 50+ common attack patterns:</p>
      
      <div class="grid md:grid-cols-2 gap-4 mb-6">
        <div class="bg-blue-50 p-4 rounded-lg">
          <h3 class="font-bold mb-2">Azure Sentinel</h3>
          <ul class="list-checkmark pl-5">
            <li>93% detection rate for identity attacks</li>
            <li>Strong Microsoft 365 integration</li>
            <li>Advanced UEBA capabilities</li>
          </ul>
        </div>
        <div class="bg-orange-50 p-4 rounded-lg">
          <h3 class="font-bold mb-2">AWS GuardDuty</h3>
          <ul class="list-checkmark pl-5">
            <li>97% detection for S3 compromise</li>
            <li>Better container runtime protection</li>
            <li>Lower false positive rate (4.2%)</li>
          </ul>
        </div>
      </div>

      <h2>Implementation Considerations</h2>
      <pre><code class="language-terraform"># Example Sentinel alert rule
resource "azurerm_sentinel_alert_rule_ms_security_incident" "example" {
  name                       = "CriticalAlerts"
  log_analytics_workspace_id = azurerm_log_analytics_workspace.example.id
  product_filter             = "Microsoft Cloud App Security"
  severity_filter            = ["High"]
}</code></pre>

      <h2>Cost Comparison</h2>
      <table class="min-w-full border">
        <thead><tr><th class="border px-4 py-2">Metric</th><th class="border px-4 py-2">Azure Sentinel</th><th class="border px-4 py-2">AWS GuardDuty</th></tr></thead>
        <tbody>
          <tr><td class="border px-4 py-2">Base Cost</td><td class="border px-4 py-2">$2.30/GB</td><td class="border px-4 py-2">$4.00/million events</td></tr>
          <tr><td class="border px-4 py-2">Enterprise Deployment</td><td class="border px-4 py-2">~$18k/month</td><td class="border px-4 py-2">~$12k/month</td></tr>
        </tbody>
      </table>
    `
  },
  {
    id: 6,
    title: "GCP Organization Policies: Enforcing Security at Scale",
    excerpt: "How to implement and automate security guardrails across Google Cloud organizations and folders.",
    date: "February 14, 2025",
    readTime: "7 min read",
    tags: ["GCP", "Security", "Automation"],
    image: "/blog-gcp-policies.jpg",
    content: `
      <h2>Critical GCP Organization Policies</h2>
      <p>After auditing 37 GCP environments, these policies prevented 89% of common misconfigurations:</p>
      
      <div class="space-y-4 mb-6">
        <div class="p-4 border-l-4 border-green-500 bg-green-50">
          <h3 class="font-bold">1. Resource Location Restriction</h3>
          <p>Prevent deployment outside approved regions</p>
        </div>
        
        <div class="p-4 border-l-4 border-blue-500 bg-blue-50">
          <h3 class="font-bold">2. Disable Service Account Key Creation</h3>
          <p>Force workload identity federation</p>
        </div>
      </div>

      <h2>Automation with Terraform</h2>
      <pre><code class="language-terraform">resource "google_organization_policy" "require_os_login" {
  org_id     = "123456789"
  constraint = "constraints/compute.requireOsLogin"

  boolean_policy {
    enforced = true
  }
}</code></pre>

      <h2>Monitoring Policy Violations</h2>
      <p>Setup real-time alerts using:</p>
      <ul class="list-disc pl-5">
        <li>Cloud Monitoring custom metrics</li>
        <li>Security Command Center notifications</li>
        <li>Terraform compliance checks in CI/CD</li>
      </ul>
    `
  },
  {
    id: 7,
    title: "Terraform Secure Modules: Building Reusable Security Patterns",
    excerpt: "Design principles for creating Terraform modules that enforce security best practices by default.",
    date: "January 30, 2025",
    readTime: "11 min read",
    tags: ["Terraform", "IaC", "DevSecOps"],
    image: "/blog-terraform-modules.jpg",
    content: `
      <h2>Secure Module Design Principles</h2>
      <div class="grid md:grid-cols-3 gap-4 mb-6">
        <div class="border p-4 rounded-lg">
          <h3 class="font-bold mb-2">1. Secure By Default</h3>
          <p>Encryption enabled, public access blocked, least privilege IAM</p>
        </div>
        <div class="border p-4 rounded-lg">
          <h3 class="font-bold mb-2">2. Explicit Overrides</h3>
          <p>Require conscious decisions to reduce security</p>
        </div>
        <div class="border p-4 rounded-lg">
          <h3 class="font-bold mb-2">3. Compliance Built-in</h3>
          <p>Pre-configured for CIS, NIST, SOC2</p>
        </div>
      </div>

      <h2>Example: Secure S3 Module</h2>
      <pre><code class="language-terraform">module "secure_s3" {
  source  = "hunterfortress/secure-s3/aws"
  version = "2.1.0"

  name_prefix = "prod-finance"
  allow_public_access = false # Override would fail policy check
  
  # Automatically enables:
  # - Bucket encryption
  # - Object locking
  # - Access logging
  # - Versioning
}</code></pre>

      <h2>Module Testing Framework</h2>
      <p>Our validation pipeline includes:</p>
      <ul class="list-checkmark pl-5">
        <li>tfsec static analysis</li>
        <li>Sentinel policy checks</li>
        <li>Conftest OPA validation</li>
        <li>Attack simulation with TerraGoat</li>
      </ul>
    `
  },
  {
    id: 8,
    title: "Multi-Cloud Identity Bridge: Azure AD + AWS IAM + GCP IAM",
    excerpt: "Implementing centralized identity management across AWS, Azure, and Google Cloud platforms.",
    date: "December 5, 2024",
    readTime: "14 min read",
    tags: ["Multi-Cloud", "IAM", "Azure AD"],
    image: "/blog-multicloud-iam.jpg",
    content: `
      <h2>The Multi-Cloud Identity Challenge</h2>
      <p>Enterprises using multiple clouds face:</p>
      <ul class="list-disc pl-5">
        <li>Credential sprawl (average 3.7 identities per employee)</li>
        <li>Inconsistent access policies</li>
        <li>No centralized audit trail</li>
      </ul>

      <h2>Reference Architecture</h2>
      <div class="bg-gray-800 text-white p-4 rounded-lg mb-6">
        <pre><code class="language-text">Azure AD (Identity Source) → 
  ├─ AWS via SAML + IAM Roles
  ├─ GCP via Workforce Identity Federation
  └─ Audit Logs to SIEM</code></pre>
      </div>

      <h2>Terraform Implementation</h2>
      <pre><code class="language-terraform"># AWS IAM Identity Provider
resource "aws_iam_saml_provider" "azure_ad" {
  name                   = "AzureAD"
  saml_metadata_document = file("azure_ad_metadata.xml")
}

# GCP Workforce Identity Pool
resource "google_iam_workload_identity_pool" "azure_ad" {
  provider                  = google-beta
  workload_identity_pool_id = "azure-ad-pool"
}</code></pre>

      <h2>Results</h2>
      <div class="bg-gray-100 p-4 rounded-lg">
        <div class="flex justify-between mb-1">
          <span>Reduction in Privileged Identities:</span>
          <span class="font-bold">76%</span>
        </div>
        <div class="h-2 bg-gray-300 rounded-full">
          <div class="h-2 bg-blue-500 rounded-full" style="width: 76%"></div>
        </div>
      </div>
    `
  },
  {
    id: 9,
    title: "Serverless Security: Protecting AWS Lambda, Azure Functions, and Cloud Run",
    excerpt: "Security patterns for serverless architectures across major cloud providers.",
    date: "November 18, 2024",
    readTime: "8 min read",
    tags: ["Serverless", "AWS Lambda", "Azure Functions"],
    image: "/blog-serverless-security.jpg",
    content: `
      <h2>Serverless Threat Model</h2>
      <p>Unique risks in serverless environments:</p>
      <div class="grid md:grid-cols-3 gap-4 mb-6">
        <div class="border p-3 rounded-lg bg-red-50">
          <h3 class="font-bold">1. Event Injection</h3>
          <p>Malformed event payloads</p>
        </div>
        <div class="border p-3 rounded-lg bg-yellow-50">
          <h3 class="font-bold">2. Over-Permissioned</h3>
          <p>Excessive function roles</p>
        </div>
        <div class="border p-3 rounded-lg bg-blue-50">
          <h3 class="font-bold">3. Cold Start Attacks</h3>
          <p>Timing vulnerabilities</p>
        </div>
      </div>

      <h2>Provider-Specific Protections</h2>
      <table class="min-w-full border mb-6">
        <thead><tr><th class="border px-4 py-2">Control</th><th class="border px-4 py-2">AWS</th><th class="border px-4 py-2">Azure</th><th class="border px-4 py-2">GCP</th></tr></thead>
        <tbody>
          <tr><td class="border px-4 py-2">IAM</td><td class="border px-4 py-2">Lambda Resource Policies</td><td class="border px-4 py-2">Managed Identities</td><td class="border px-4 py-2">Service Account Constraints</td></tr>
          <tr><td class="border px-4 py-2">Network</td><td class="border px-4 py-2">VPC + SG</td><td class="border px-4 py-2">VNet Integration</td><td class="border px-4 py-2">Serverless VPC Connector</td></tr>
        </tbody>
      </table>

      <h2>Code Example: Secure Lambda</h2>
      <pre><code class="language-javascript">// Validate event payloads
module.exports.handler = async (event) => {
  if (!validateInput(event)) {
    throw new Error("Invalid input");
  }
  
  // Least privilege execution
  const data = await s3.getObject({
    Bucket: process.env.STORAGE_BUCKET,
    Key: event.key
  }).promise();
  
  return processData(data);
};</code></pre>
    `
  },
  {
    id: 10,
    title: "Azure Policy vs AWS SCP: Enterprise Governance Compared",
    excerpt: "Deep dive into policy enforcement capabilities for Azure and AWS multi-account environments.",
    date: "October 29, 2024",
    readTime: "10 min read",
    tags: ["Azure", "AWS", "Governance"],
    image: "/blog-policy-compare.jpg",
    content: `
      <h2>Policy Coverage Comparison</h2>
      <p>Evaluated 120 common enterprise requirements:</p>
      <div class="flex items-center mb-4">
        <div class="w-1/2 pr-4">
          <div class="flex justify-between mb-1">
            <span class="text-blue-700 font-medium">Azure Policy</span>
            <span class="font-bold">92% coverage</span>
          </div>
          <div class="h-3 bg-gray-200 rounded-full">
            <div class="h-3 bg-blue-600 rounded-full" style="width: 92%"></div>
          </div>
        </div>
        <div class="w-1/2 pl-4">
          <div class="flex justify-between mb-1">
            <span class="text-orange-600 font-medium">AWS SCP</span>
            <span class="font-bold">78% coverage</span>
          </div>
          <div class="h-3 bg-gray-200 rounded-full">
            <div class="h-3 bg-orange-500 rounded-full" style="width: 78%"></div>
          </div>
        </div>
      </div>

      <h2>Key Differences</h2>
      <div class="grid md:grid-cols-2 gap-4 mb-6">
        <div class="bg-blue-50 p-4 rounded-lg">
          <h3 class="font-bold mb-2">Azure Policy Strengths</h3>
          <ul class="list-checkmark pl-5">
            <li>Remediation tasks</li>
            <li>Detailed compliance reporting</li>
            <li>Resource provider coverage</li>
          </ul>
        </div>
        <div class="bg-orange-50 p-4 rounded-lg">
          <h3 class="font-bold mb-2">AWS SCP Advantages</h3>
          <ul class="list-checkmark pl-5">
            <li>Service control integration</li>
            <li>Condition keys flexibility</li>
            <li>Organization-wide deployment</li>
          </ul>
        </div>
      </div>

      <h2>Example: Enforce Encryption</h2>
      <div class="grid md:grid-cols-2 gap-4">
        <div>
          <h3 class="font-bold mb-2">Azure Policy</h3>
          <pre><code class="language-json">{
  "if": {
    "allOf": [
      { "field": "type", "equals": "Microsoft.Storage/storageAccounts" },
      { "not": { "field": "Microsoft.Storage/storageAccounts/encryption.keySource", "equals": "Microsoft.Keyvault" }
    ]
  },
  "then": { "effect": "deny" }
}</code></pre>
        </div>
        <div>
          <h3 class="font-bold mb-2">AWS SCP</h3>
          <pre><code class="language-json">{
  "Version": "2012-10-17",
  "Statement": [{
    "Effect": "Deny",
    "Action": ["s3:PutObject"],
    "Resource": "*",
    "Condition": {
      "Null": { "s3:x-amz-server-side-encryption": true }
    }
  }]
}</code></pre>
        </div>
      </div>
    `
  },
  {
    id: 11,
    title: "GCP BeyondCorp: Implementing Zero Trust for Google Cloud",
    excerpt: "Practical guide to deploying Google's BeyondCorp zero trust model in enterprise environments.",
    date: "September 14, 2024",
    readTime: "12 min read",
    tags: ["GCP", "Zero Trust", "BeyondCorp"],
    image: "/blog-beyondcorp.jpg",
    content: `
      <h2>BeyondCorp Implementation Framework</h2>
      <ol class="list-decimal pl-5 mb-6">
        <li class="mb-2"><strong>Context-Aware Access</strong> - Device + user attributes</li>
        <li class="mb-2"><strong>Identity-Aware Proxy</strong> - Replace VPNs</li>
        <li class="mb-2"><strong>Endpoint Verification</strong> - Device trust</li>
        <li><strong>Continuous AuthN</strong> - Session revalidation</li>
      </ol>

      <h2>Key Components</h2>
      <div class="grid md:grid-cols-2 gap-4 mb-6">
        <div>
          <h3 class="font-bold mb-2">1. IAP Configuration</h3>
          <pre><code class="language-terraform">resource "google_iap_web_iam_binding" "prod_app" {
  project = google_project.prod.project_id
  role    = "roles/iap.httpsResourceAccessor"
  members = ["group:prod-access@your-domain.com"]
}</code></pre>
        </div>
        <div>
          <h3 class="font-bold mb-2">2. Access Level Conditions</h3>
          <pre><code class="language-yaml">accessLevels:
- name: corp_device_access
  title: Corporate Devices Only
  basic:
    conditions:
    - devicePolicy:
        requireScreenLock: true
        allowedEncryptionStatuses: [ENCRYPTED]</code></pre>
        </div>
      </div>

      <h2>Migration Results</h2>
      <p>Financial services client metrics:</p>
      <ul class="list-checkmark pl-5">
        <li>VPN usage reduced by 92%</li>
        <li>Access policy changes from 3 weeks to 15 minutes</li>
        <li>Phishing success rate dropped to 0.2%</li>
      </ul>
    `
  },
  {
    id: 12,
    title: "Terraform Security: Preventing Drift and Configuration Attacks",
    excerpt: "Advanced techniques to secure your Terraform pipelines against drift and malicious changes.",
    date: "August 22, 2024",
    readTime: "9 min read",
    tags: ["Terraform", "DevSecOps", "Supply Chain"],
    image: "/blog-terraform-security.jpg",
    content: `
      <h2>Terraform Attack Vectors</h2>
      <div class="p-4 bg-red-50 border-l-4 border-red-500 mb-6">
        <h3 class="font-bold mb-2">Recent Supply Chain Attacks</h3>
        <p>Malicious modules downloaded 14,000+ times before detection</p>
      </div>

      <h2>Defense-in-Depth Approach</h2>
      <div class="grid md:grid-cols-3 gap-4 mb-6">
        <div class="border p-3 rounded-lg">
          <h3 class="font-bold mb-2">1. Pipeline Security</h3>
          <ul class="list-disc pl-5 text-sm">
            <li>Signed plan artifacts</li>
            <li>Branch protection</li>
          </ul>
        </div>
        <div class="border p-3 rounded-lg">
          <h3 class="font-bold mb-2">2. State Protection</h3>
          <ul class="list-disc pl-5 text-sm">
            <li>Encrypted remote state</li>
            <li>State change approvals</li>
          </ul>
        </div>
        <div class="border p-3 rounded-lg">
          <h3 class="font-bold mb-2">3. Module Validation</h3>
          <ul class="list-disc pl-5 text-sm">
            <li>Hash verification</li>
            <li>Private registry</li>
          </ul>
        </div>
      </div>

      <h2>Implementation Example</h2>
      <pre><code class="language-terraform"># Require module provenance
module "vpc" {
  source  = "app.terraform.io/secure-corp/vpc/aws"
  version = "1.2.0"

  # Verified hashes
  hashes = [
    "h1:ab...123",
    "h1:cd...456"
  ]
}</code></pre>

      <h2>Security Toolchain</h2>
      <p>Recommended stack:</p>
      <ul class="list-checkmark pl-5">
        <li>tfsec for static analysis</li>
        <li>OPA Gatekeeper for policy</li>
        <li>Artifact Registry for modules</li>
        <li>Cosign for signatures</li>
      </ul>
    `
  },
  {
  id: 13,
  title: "Azure Arc: Securing Hybrid Cloud Environments",
  excerpt: "Security considerations for managing on-prem and multi-cloud resources through Azure Arc.",
  date: "July 8, 2024",
  readTime: "7 min read",
  tags: ["Azure", "Hybrid Cloud", "Compliance"],
  image: "/blog-azure-arc.jpg",
  content: `
    <h2>Azure Arc Security Model</h2>
    <p>Key components for hybrid security:</p>
    <div class="space-y-4 mb-6">
      <div class="p-4 border-l-4 border-blue-500 bg-blue-50">
        <h3 class="font-bold">1. Managed Identity</h3>
        <p>Azure AD integration for on-prem servers</p>
      </div>
      <div class="p-4 border-l-4 border-purple-500 bg-purple-50">
        <h3 class="font-bold">2. Policy Enforcement</h3>
        <p>Extend Azure Policy beyond Azure</p>
      </div>
    </div>

    <h2>Implementation Steps</h2>
    <ol class="list-decimal pl-5 mb-6">
      <li>Install Connected Machine agent</li>
      <li>Assign Azure Policy initiatives</li>
      <li>Configure Log Analytics workspace</li>
      <li>Enable Azure Defender</li>
    </ol>

    <h2>Example: On-Prem Compliance</h2>
    <pre><code class="language-powershell"># Install Arc agent
& ./InstallAzureArcAgent.ps1 ${'`'}
  -TenantId "your-tenant" ${'`'}
  -SubscriptionId "your-sub" ${'`'}
  -ResourceGroup "on-prem-servers" ${'`'}
  -Location "eastus"</code></pre>

    <h2>Security Benefits</h2>
    <div class="bg-gray-100 p-4 rounded-lg">
      <div class="flex justify-between mb-1">
        <span>Reduction in Compliance Gaps:</span>
        <span class="font-bold">64%</span>
      </div>
      <div class="h-2 bg-gray-300 rounded-full">
        <div class="h-2 bg-blue-500 rounded-full" style="width: 64%"></div>
      </div>
    </div>
  `
},
  {
    id: 14,
    title: "AWS IAM Access Analyzer vs GCP Policy Troubleshooter",
    excerpt: "Comparing cloud provider tools for identifying and resolving over-permissioned IAM policies.",
    date: "June 1, 2024",
    readTime: "6 min read",
    tags: ["AWS", "GCP", "IAM"],
    image: "/blog-iam-tools.jpg",
    content: `
      <h2>Permission Analysis Capabilities</h2>
      <table class="min-w-full border mb-6">
        <thead><tr><th class="border px-4 py-2">Feature</th><th class="border px-4 py-2">AWS Access Analyzer</th><th class="border px-4 py-2">GCP Policy Troubleshooter</th></tr></thead>
        <tbody>
          <tr><td class="border px-4 py-2">Unused Permissions</td><td class="border px-4 py-2">✓ Last 90 days</td><td class="border px-4 py-2">✓ Last 60 days</td></tr>
          <tr><td class="border px-4 py-2">External Access</td><td class="border px-4 py-2">✓ Detailed findings</td><td class="border px-4 py-2">Basic only</td></tr>
        </tbody>
      </table>

      <h2>Example Findings</h2>
      <div class="grid md:grid-cols-2 gap-4 mb-6">
        <div class="bg-orange-50 p-4 rounded-lg">
          <h3 class="font-bold mb-2">AWS Critical Finding</h3>
          <pre><code class="language-json">{
  "finding": "S3_BUCKET_PUBLIC",
  "resource": "arn:aws:s3:::insecure-bucket",
  "action": "s3:GetObject",
  "isPublic": true
}</code></pre>
        </div>
        <div class="bg-blue-50 p-4 rounded-lg">
          <h3 class="font-bold mb-2">GCP Recommendation</h3>
          <pre><code class="language-json">{
  "recommendation": "REMOVE_ROLE",
  "role": "roles/editor",
  "unusedPermissions": ["compute.instances.create"]
}</code></pre>
        </div>
      </div>

      <h2>Automation Options</h2>
      <p>Both support remediation through:</p>
      <ul class="list-disc pl-5">
        <li>Terraform integration</li>
        <li>Cloud Functions/Lambda triggers</li>
        <li>Export to SIEM/SOAR</li>
      </ul>
    `
  },
  {
    id: 15,
    title: "Container Security Scanning: CI/CD Pipeline Integration",
    excerpt: "Implementing automated container vulnerability scanning in CI/CD pipelines with actionable results.",
    date: "May 15, 2024",
    readTime: "7 min read",
    tags: ["Containers", "CI/CD", "Security Scanning"],
    image: "/blog-container-scanning.jpg",
    content: `
      <h2>The Container Security Gap</h2>
      <p>Research shows 63% of production containers have at least one critical vulnerability, with average remediation time of 97 days.</p>
      
      <h2>Pipeline Integration Strategy</h2>
      <div class="grid md:grid-cols-2 gap-4 mb-6">
        <div class="bg-blue-50 p-4 rounded-lg">
          <h3 class="font-bold mb-2">1. Pre-Build Scanning</h3>
          <p>Base image validation and policy enforcement:</p>
          <ul class="list-disc pl-5">
            <li>Approved base images only</li>
            <li>Vulnerability threshold blocking</li>
            <li>License compliance checks</li>
          </ul>
        </div>
        <div class="bg-green-50 p-4 rounded-lg">
          <h3 class="font-bold mb-2">2. Post-Build Analysis</h3>
          <p>Runtime-focused security:</p>
          <ul class="list-disc pl-5">
            <li>Secrets detection</li>
            <li>Configuration hardening</li>
            <li>Dependency analysis</li>
          </ul>
        </div>
      </div>

      <h2>GitHub Actions Implementation</h2>
      <pre><code class="language-yaml">name: Container Security Scan
on: [push]
jobs:
  security-scan:
    runs-on: ubuntu-latest
    steps:
    - uses: actions/checkout@v3
    - name: Build container
      run: docker build -t myapp:${'$'}{{'$'}{ github.sha }} .
    - name: Scan for vulnerabilities
      uses: aquasecurity/trivy-action@master
      with:
        image-ref: 'myapp:${'$'}{{'$'}{ github.sha }}'
        format: 'sarif'
        output: 'trivy-results.sarif'
    - name: Upload results
      uses: github/codeql-action/upload-sarif@v2
      with:
        sarif_file: 'trivy-results.sarif'</code></pre>

      <h2>Results Achieved</h2>
      <p>E-commerce platform implementation:</p>
      <table class="min-w-full border">
        <thead><tr><th class="border px-4 py-2">Metric</th><th class="border px-4 py-2">Before</th><th class="border px-4 py-2">After</th></tr></thead>
        <tbody>
          <tr><td class="border px-4 py-2">Critical Vulnerabilities</td><td class="border px-4 py-2">14.2 per image</td><td class="border px-4 py-2">0.8 per image</td></tr>
          <tr><td class="border px-4 py-2">Remediation Time</td><td class="border px-4 py-2">97 days</td><td class="border px-4 py-2">2.3 days</td></tr>
        </tbody>
      </table>
    `
  },
  {
    id: 16,
    title: "Secrets Management: Beyond Environment Variables",
    excerpt: "Modern approaches to secrets management in cloud-native applications with rotation automation.",
    date: "April 22, 2024",
    readTime: "9 min read",
    tags: ["Secrets", "Security", "DevOps"],
    image: "/blog-secrets-management.jpg",
    content: `
      <h2>The Problem with Traditional Secrets</h2>
      <p>Environment variables and config files lead to secrets sprawl - our audit found 83% of companies had secrets exposed in source code.</p>
      
      <h2>Modern Secrets Management Architecture</h2>
      <div class="space-y-4 mb-6">
        <div class="p-4 border-l-4 border-blue-500 bg-blue-50">
          <h3 class="font-bold">1. Centralized Vault</h3>
          <p>AWS Secrets Manager, Azure Key Vault, or HashiCorp Vault with automated rotation</p>
        </div>
        <div class="p-4 border-l-4 border-green-500 bg-green-50">
          <h3 class="font-bold">2. Runtime Injection</h3>
          <p>Secrets delivered directly to applications without persistent storage</p>
        </div>
        <div class="p-4 border-l-4 border-purple-500 bg-purple-50">
          <h3 class="font-bold">3. Audit Trail</h3>
          <p>Complete access logging and change history for compliance</p>
        </div>
      </div>

      <h2>AWS Secrets Manager Integration</h2>
      <pre><code class="language-terraform">resource "aws_secretsmanager_secret" "database" {
  name                    = "prod/database/credentials"
  description             = "Production database credentials"
  recovery_window_in_days = 0 # Immediate deletion

  rotation_lambda_arn = aws_lambda_function.rotator.arn
  rotation_rules {
    automatically_after_days = 30
  }
}

resource "aws_lambda_function" "rotator" {
  filename      = "rotator.zip"
  function_name = "secret-rotator"
  role          = aws_iam_role.rotator.arn
  handler       = "index.handler"
  runtime       = "nodejs14.x"
}</code></pre>

      <h2>Implementation Benefits</h2>
      <ul class="list-checkmark pl-5">
        <li>Reduced secret exposure by 92%</li>
        <li>Automated rotation every 30 days</li>
        <li>Eliminated hardcoded secrets in codebase</li>
        <li>Compliant with SOC2 and ISO27001 requirements</li>
      </ul>
    `
  },
  {
    id: 17,
    title: "Cloud Security Posture Management (CSPM) Implementation Guide",
    excerpt: "Building continuous compliance monitoring across AWS, Azure, and GCP with automated remediation.",
    date: "March 30, 2024",
    readTime: "11 min read",
    tags: ["CSPM", "Compliance", "Cloud Security"],
    image: "/blog-cspm.jpg",
    content: `
      <h2>The CSPM Imperative</h2>
      <p>Organizations experience an average of 12.4 misconfigurations per cloud account, with 31% considered high or critical risk.</p>
      
      <h2>Multi-Cloud CSPM Architecture</h2>
      <div class="bg-gray-800 text-white p-4 rounded-lg mb-6">
        <pre><code class="language-text">Cloud Accounts → CSPM Scanner → Findings Database →
  ├─ Alerting (Slack/Teams/Email)
  ├─ Ticketing (Jira/ServiceNow)
  └─ Automated Remediation (Lambda/Functions)</code></pre>
      </div>

      <h2>Key Security Checks</h2>
      <div class="grid md:grid-cols-3 gap-4 mb-6">
        <div class="border p-3 rounded-lg">
          <h3 class="font-bold">AWS</h3>
          <ul class="list-disc pl-5 text-sm">
            <li>S3 public access</li>
            <li>Security group misconfigurations</li>
            <li>IAM policy violations</li>
          </ul>
        </div>
        <div class="border p-3 rounded-lg">
          <h3 class="font-bold">Azure</h3>
          <ul class="list-disc pl-5 text-sm">
            <li>NSG rule auditing</li>
            <li>Storage account security</li>
            <li>SQL database encryption</li>
          </ul>
        </div>
        <div class="border p-3 rounded-lg">
          <h3 class="font-bold">GCP</h3>
          <ul class="list-disc pl-5 text-sm">
            <li>Firewall rule review</li>
            <li>Bucket ACL checks</li>
            <li>Service account permissions</li>
          </ul>
        </div>
      </div>

      <h2>Automated Remediation Example</h2>
      <pre><code class="language-python">def remediate_s3_bucket(event):
    # Get the non-compliant bucket
    bucket_name = event['detail']['resourceId']
    
    # Apply security fixes
    s3.put_public_access_block(
        Bucket=bucket_name,
        PublicAccessBlockConfiguration={
            'BlockPublicAcls': True,
            'IgnorePublicAcls': True,
            'BlockPublicPolicy': True,
            'RestrictPublicBuckets': True
        }
    )
    
    # Log the remediation
    cloudwatch.put_metric_data(
        Namespace='CSPM/Remediation',
        MetricData=[{
            'MetricName': 'RemediatedResources',
            'Value': 1,
            'Unit': 'Count'
        }]
    )</code></pre>

      <h2>Results</h2>
      <div class="bg-gray-100 p-4 rounded-lg">
        <div class="flex justify-between mb-1">
          <span>Misconfiguration Reduction:</span>
          <span class="font-bold">89%</span>
        </div>
        <div class="h-2 bg-gray-300 rounded-full">
          <div class="h-2 bg-green-500 rounded-full" style="width: 89%"></div>
        </div>
      </div>
    `
  },
  {
    id: 18,
    title: "OWASP Top 10 for APIs: Practical Mitigation Strategies",
    excerpt: "Implementing security controls for the OWASP API Security Top 10 vulnerabilities in production environments.",
    date: "February 18, 2024",
    readTime: "8 min read",
    tags: ["API Security", "OWASP", "Web Security"],
    image: "/blog-owasp-api.jpg",
    content: `
      <h2>API Security Crisis</h2>
      <p>API attacks increased 681% in 2023, with 94% of organizations experiencing security incidents related to API vulnerabilities.</p>
      
      <h2>Critical OWASP API Risks & Mitigations</h2>
      <div class="space-y-4 mb-6">
        <div class="p-4 border-l-4 border-red-500 bg-red-50">
          <h3 class="font-bold">API1: Broken Object Level Authorization</h3>
          <p><strong>Solution:</strong> Implement centralized authorization checks with resource-based access control</p>
        </div>
        <div class="p-4 border-l-4 border-orange-500 bg-orange-50">
          <h3 class="font-bold">API2: Broken Authentication</h3>
          <p><strong>Solution:</strong> Multi-factor authentication, strong password policies, and token rotation</p>
        </div>
        <div class="p-4 border-l-4 border-blue-500 bg-blue-50">
          <h3 class="font-bold">API3: Excessive Data Exposure</h3>
          <p><strong>Solution:</strong> Response filtering and field-level authorization</p>
        </div>
      </div>

      <h2>API Gateway Security Configuration</h2>
      <pre><code class="language-terraform">resource "aws_api_gateway_rest_api" "secure_api" {
  name        = "secure-product-api"
  description = "API with OWASP Top 10 protections"

  endpoint_configuration {
    types = ["REGIONAL"]
  }
}

resource "aws_wafv2_web_acl" "api_protection" {
  name  = "api-owasp-protection"
  scope = "REGIONAL"

  default_action {
    allow {}
  }

  rule {
    name     = "OWASP-API-Common"
    priority = 1
    override_action {
      count {}
    }
    statement {
      managed_rule_group_statement {
        name        = "AWSManagedRulesCommonRuleSet"
        vendor_name = "AWS"
      }
    }
    visibility_config {
      cloudwatch_metrics_enabled = true
      metric_name                = "OWASP-API-Common"
      sampled_requests_enabled   = true
    }
  }
}</code></pre>

      <h2>Implementation Results</h2>
      <table class="min-w-full border">
        <thead><tr><th class="border px-4 py-2">Metric</th><th class="border px-4 py-2">Before</th><th class="border px-4 py-2">After</th></tr></thead>
        <tbody>
          <tr><td class="border px-4 py-2">API Security Incidents</td><td class="border px-4 py-2">14/month</td><td class="border px-4 py-2">0/month</td></tr>
          <tr><td class="border px-4 py-2">Penetration Test Findings</td><td class="border px-4 py-2">27 critical</td><td class="border px-4 py-2">2 low</td></tr>
        </tbody>
      </table>
    `
  },
  {
    id: 19,
    title: "Security Chaos Engineering: Breaking Your Systems on Purpose",
    excerpt: "Implementing controlled failure injection to build resilient security systems and response processes.",
    date: "January 12, 2024",
    readTime: "10 min read",
    tags: ["Chaos Engineering", "Resilience", "Testing"],
    image: "/blog-chaos-engineering.jpg",
    content: `
      <h2>What is Security Chaos Engineering?</h2>
      <p>Proactively injecting security failures to validate controls, improve incident response, and build resilience.</p>
      
      <h2>Security Chaos Experiments</h2>
      <div class="grid md:grid-cols-2 gap-4 mb-6">
        <div class="bg-blue-50 p-4 rounded-lg">
          <h3 class="font-bold mb-2">1. Control Validation</h3>
          <ul class="list-disc pl-5">
            <li>Disable security agents</li>
            <li>Block monitoring endpoints</li>
            <li>Corrupt log files</li>
          </ul>
        </div>
        <div class="bg-green-50 p-4 rounded-lg">
          <h3 class="font-bold mb-2">2. Response Testing</h3>
          <ul class="list-disc pl-5">
            <li>Simulate breach scenarios</li>
            <li>Test communication channels</li>
            <li>Validate runbook effectiveness</li>
          </ul>
        </div>
      </div>

      <h2>Chaos Toolkit Experiment Definition</h2>
      <pre><code class="language-yaml">version: 1.0.0
title: "Security Agent Failure Simulation"
description: "What happens if our EDR solution stops working?"
tags: ["security", "resilience"]

method:
- type: action
  name: "disable-edr-agent"
  provider:
    type: "process"
    path: "systemctl"
    arguments: "stop edr-agent"
  
- type: probe
  name: "verify-agent-status"
  provider:
    type: "process"
    path: "systemctl"
    arguments: "is-active edr-agent"
  tolerance: "inactive"
  
- type: action
  name: "wait-for-alert"
  provider:
    type: "python"
    module: "time"
    func: "sleep"
    arguments: 300
  
- type: probe
  name: "check-security-alerts"
  provider:
    type: "http"
    url: "https://siem.example.com/alerts"
    method: "GET"
  tolerance:
    - type: "jsonpath"
      path: "$.alerts[?(@.severity=='high')]"
      expect: 1</code></pre>

      <h2>Measured Improvements</h2>
      <ul class="list-checkmark pl-5">
        <li>MTTR reduced from 4 hours to 22 minutes</li>
        <li>Security control coverage increased to 98%</li>
        <li>False positive rate decreased by 67%</li>
        <li>Team confidence score improved from 5.2 to 8.7/10</li>
      </ul>
    `
  },
  {
    id: 20,
    title: "Cloud Data Loss Prevention (DLP) Implementation Guide",
    excerpt: "Protecting sensitive data across cloud storage, databases, and applications with automated classification.",
    date: "December 5, 2023",
    readTime: "12 min read",
    tags: ["DLP", "Data Protection", "Compliance"],
    image: "/blog-dlp.jpg",
    content: `
      <h2>The Data Protection Challenge</h2>
      <p>The average enterprise has 23.5 PB of data in cloud storage, with 21% containing sensitive information requiring protection.</p>
      
      <h2>Cloud DLP Architecture</h2>
      <div class="bg-gray-800 text-white p-4 rounded-lg mb-6">
        <pre><code class="language-text">Data Sources → DLP Scanner → Classification Engine →
  ├─ Encryption (BYOK/CMK)
  ├─ Masking/Tokenization
  ├─ Access Controls
  └─ Audit Logging</code></pre>
      </div>

      <h2>Multi-Cloud DLP Implementation</h2>
      <div class="grid md:grid-cols-3 gap-4 mb-6">
        <div class="border p-3 rounded-lg">
          <h3 class="font-bold">AWS Macie</h3>
          <ul class="list-disc pl-5 text-sm">
            <li>S3 bucket discovery</li>
            <li>PII detection</li>
            <li>Custom data identifiers</li>
          </ul>
        </div>
        <div class="border p-3 rounded-lg">
          <h3 class="font-bold">Azure Purview</h3>
          <ul class="list-disc pl-5 text-sm">
            <li>Multi-source scanning</li>
            <li>Sensitivity labels</li>
            <li>GDPR/CCPA compliance</li>
          </ul>
        </div>
        <div class="border p-3 rounded-lg">
          <h3 class="font-bold">GCP DLP API</h3>
          <ul class="list-disc pl-5 text-sm">
            <li>Real-time scanning</li>
            <li>De-identification</li>
            <li>Risk analysis</li>
          </ul>
        </div>
      </div>

      <h2>Terraform DLP Configuration</h2>
      <pre><code class="language-terraform">resource "aws_macie2_classification_job" "s3_pii_scan" {
  job_type = "SCHEDULED"
  name     = "s3-pii-scan"

  s3_job_definition {
    bucket_definitions {
      account_id = data.aws_caller_identity.current.account_id
      buckets    = ["sensitive-data-bucket"]
    }

    scoping {
      excludes {
        and {
          simple_scope_term {
            comparator = "EQ"
            key        = "OBJECT_EXTENSION"
            values     = ["jpg", "png", "gif"]
          }
        }
      }
    }
  }

  schedule_frequency {
    daily_schedule = true
  }
}</code></pre>

      <h2>Protection Results</h2>
      <div class="bg-gray-100 p-4 rounded-lg">
        <div class="flex justify-between mb-1">
          <span>Sensitive Data Protected:</span>
          <span class="font-bold">99.7%</span>
        </div>
        <div class="h-2 bg-gray-300 rounded-full">
          <div class="h-2 bg-blue-500 rounded-full" style="width: 99.7%"></div>
        </div>
      </div>
    `
  },
  {
    id: 21,
    title: "Zero Day Response: Building an Effective Vulnerability Management Program",
    excerpt: "Creating processes and tools for rapid response to critical vulnerabilities and zero-day threats.",
    date: "November 20, 2023",
    readTime: "9 min read",
    tags: ["Vulnerability", "Zero Day", "Incident Response"],
    image: "/blog-zero-day.jpg",
    content: `
      <h2>The Zero-Day Reality</h2>
      <p>There were 76 zero-day vulnerabilities exploited in 2023, with average enterprise response time of 16.4 days.</p>
      
      <h2>Zero-Day Response Framework</h2>
      <div class="space-y-4 mb-6">
        <div class="p-4 border-l-4 border-red-500 bg-red-50">
          <h3 class="font-bold">1. Preparation</h3>
          <p>Asset inventory, patch management infrastructure, communication channels</p>
        </div>
        <div class="p-4 border-l-4 border-orange-500 bg-orange-50">
          <h3 class="font-bold">2. Detection</h3>
          <p>Threat intelligence feeds, vulnerability scanning, SIEM alerts</p>
        </div>
        <div class="p-4 border-l-4 border-green-500 bg-green-50">
          <h3 class="font-bold">3. Response</h3>
          <p>Patch deployment, workarounds, mitigation controls</p>
        </div>
      </div>

      <h2>Automated Vulnerability Response</h2>
      <pre><code class="language-python">def handle_zero_day_alert(vulnerability):
    # Query affected assets
    affected_assets = query_assets_by_cpe(vulnerability['cpe'])
    
    # Determine criticality
    criticality = calculate_criticality(
        vulnerability['cvss'],
        affected_assets['count'],
        affected_assets['sensitivity']
    )
    
    if criticality >= 8.0:
        # Emergency change process
        create_emergency_change(
            title=f"Zero-day patch: {vulnerability['id']}",
            assets=affected_assets['ids'],
            deadline=datetime.now() + timedelta(hours=24)
        )
        
        # Immediate mitigations
        apply_network_mitigations(affected_assets['network_groups'])
        
        # Communications
        notify_security_team(vulnerability)
        notify_leadership(criticality, affected_assets)
        
    elif criticality >= 4.0:
        # Standard change process
        create_standard_change(
            vulnerability['id'],
            affected_assets['ids'],
            deadline=datetime.now() + timedelta(days=7)
        )</code></pre>

      <h2>Response Time Improvements</h2>
      <table class="min-w-full border">
        <thead><tr><th class="border px-4 py-2">Metric</th><th class="border px-4 py-2">Before</th><th class="border px-4 py-2">After</th></tr></thead>
        <tbody>
          <tr><td class="border px-4 py-2">Zero-Day Response Time</td><td class="border px-4 py-2">16.4 days</td><td class="border px-4 py-2">2.1 days</td></tr>
          <tr><td class="border px-4 py-2">Critical Patch Deployment</td><td class="border px-4 py-2">8.7 days</td><td class="border px-4 py-2">1.3 days</td></tr>
        </tbody>
      </table>
    `
  },
  {
    id: 22,
    title: "Security Orchestration, Automation, and Response (SOAR) Implementation",
    excerpt: "Building automated security workflows for incident response, threat hunting, and security operations.",
    date: "October 15, 2023",
    readTime: "11 min read",
    tags: ["SOAR", "Automation", "Incident Response"],
    image: "/blog-soar.jpg",
    content: `
      <h2>The SOAR Advantage</h2>
      <p>Organizations with SOAR implementations experience 76% faster incident response and 68% reduction in alert fatigue.</p>
      
      <h2>SOAR Architecture Components</h2>
      <div class="grid md:grid-cols-3 gap-4 mb-6">
        <div class="border p-3 rounded-lg">
          <h3 class="font-bold">1. Playbooks</h3>
          <p>Automated workflows for common security scenarios</p>
        </div>
        <div class="border p-3 rounded-lg">
          <h3 class="font-bold">2. Integrations</h3>
          <p>Connections to security tools, cloud platforms, and IT systems</p>
        </div>
        <div class="border p-3 rounded-lg">
          <h3 class="font-bold">3. Case Management</h3>
          <p>Unified incident tracking and collaboration</p>
        </div>
      </div>

      <h2>Phishing Response Playbook</h2>
      <pre><code class="language-yaml">name: Phishing Email Response
description: Automated response to reported phishing emails
version: 1.0

start:
  - type: trigger
    name: phishing-reported

steps:
  - name: extract-indicators
    type: action
    action: extract_iocs
    parameters:
      email_source: $\{trigger.email}

  - name: check-reputation
    type: parallel
    actions:
      - action: url_reputation
        parameters:
          url: $\{steps.extract-indicators.urls}
      - action: file_reputation
        parameters:
          hashes: $\{steps.extract-indicators.hashes}

  - name: contain-threat
    type: action
    action: block_indicators
    parameters:
      urls: $\{steps.extract-indicators.urls}
      hashes: $\{steps.extract-indicators.hashes}
      ips: $\{steps.extract-indicators.ips}
    condition: $\{steps.check-reputation.malicious_count > 0}

  - name: create-ticket
    type: action
    action: servicenow_create_incident
    parameters:
      description: "Phishing campaign response"
      severity: "High"
      assignment_group: "security-operations"

  - name: notify-team
    type: action
    action: slack_send_message
    parameters:
      channel: "#security-incidents"
      message: "Phishing response completed - $\{steps.contain-threat.indicators_blocked} indicators blocked"</code></pre>

      <h2>Automation Benefits</h2>
      <ul class="list-checkmark pl-5">
        <li>Incident response time: 76% faster</li>
        <li>Manual effort reduction: 83%</li>
        <li>Mean time to resolution: 4.2 hours → 38 minutes</li>
        <li>Analyst capacity increase: 3.7x more incidents handled</li>
      </ul>
    `
  },
  {
    id: 23,
    title: "Cloud Network Security: Beyond Traditional Firewalls",
    excerpt: "Modern approaches to cloud network security using microsegmentation, service meshes, and zero trust principles.",
    date: "September 8, 2023",
    readTime: "10 min read",
    tags: ["Network Security", "Cloud", "Zero Trust"],
    image: "/blog-cloud-network.jpg",
    content: `
      <h2>Cloud Networking Evolution</h2>
      <p>Traditional perimeter security is obsolete in cloud environments where 78% of workloads communicate across network boundaries.</p>
      
      <h2>Modern Cloud Network Security Stack</h2>
      <div class="space-y-4 mb-6">
        <div class="p-4 border-l-4 border-blue-500 bg-blue-50">
          <h3 class="font-bold">1. Microsegmentation</h3>
          <p>Identity-based segmentation replacing IP-based rules</p>
        </div>
        <div class="p-4 border-l-4 border-green-500 bg-green-50">
          <h3 class="font-bold">2. Service Mesh</h3>
          <p>mTLS, observability, and fine-grained traffic control</p>
        </div>
        <div class="p-4 border-l-4 border-purple-500 bg-purple-50">
          <h3 class="font-bold">3. Cloud Firewalls</h3>
          <p>Native cloud provider firewall services with threat intelligence</p>
        </div>
      </div>

      <h2>Istio Service Mesh Security</h2>
      <pre><code class="language-yaml">apiVersion: security.istio.io/v1beta1
kind: AuthorizationPolicy
metadata:
  name: payment-service-auth
  namespace: production
spec:
  selector:
    matchLabels:
      app: payment-service
  action: ALLOW
  rules:
  - from:
    - source:
        principals: ["cluster.local/ns/frontend/sa/frontend-service"]
    to:
    - operation:
        methods: ["POST"]
        paths: ["/api/v1/payments"]
    when:
    - key: request.headers[Content-Type]
      values: ["application/json"]

---
apiVersion: security.istio.io/v1beta1
kind: PeerAuthentication
metadata:
  name: strict-mtls
  namespace: production
spec:
  mtls:
    mode: STRICT</code></pre>

      <h2>Security Improvements</h2>
      <div class="bg-gray-100 p-4 rounded-lg">
        <div class="flex justify-between mb-1">
          <span>Lateral Movement Prevention:</span>
          <span class="font-bold">94%</span>
        </div>
        <div class="h-2 bg-gray-300 rounded-full">
          <div class="h-2 bg-green-500 rounded-full" style="width: 94%"></div>
        </div>
      </div>
    `
  },
  {
    id: 24,
    title: "DevSecOps Metrics: Measuring Security Program Effectiveness",
    excerpt: "Key performance indicators and metrics for measuring and improving DevSecOps program effectiveness.",
    date: "August 22, 2023",
    readTime: "8 min read",
    tags: ["DevSecOps", "Metrics", "KPIs"],
    image: "/blog-devsecops-metrics.jpg",
    content: `
      <h2>What Gets Measured Gets Improved</h2>
      <p>High-performing DevSecOps teams measure 4.2x more security metrics than low performers, leading to 78% faster vulnerability remediation.</p>
      
      <h2>Critical DevSecOps Metrics</h2>
      <div class="grid md:grid-cols-2 gap-4 mb-6">
        <div class="bg-blue-50 p-4 rounded-lg">
          <h3 class="font-bold mb-2">Security Velocity</h3>
          <ul class="list-disc pl-5">
            <li>Mean Time to Remediate (MTTR)</li>
            <li>Security cycle time</li>
            <li>Deployment frequency with security</li>
          </ul>
        </div>
        <div class="bg-green-50 p-4 rounded-lg">
          <h3 class="font-bold mb-2">Security Quality</h3>
          <ul class="list-disc pl-5">
            <li>Vulnerability density</li>
            <li>False positive rate</li>
            <li>Security test coverage</li>
          </ul>
        </div>
      </div>

      <h2>Security Dashboard Implementation</h2>
      <pre><code class="language-sql">-- Security Metrics Query Example
SELECT 
  project_name,
  COUNT(*) as total_vulnerabilities,
  SUM(CASE WHEN severity = 'CRITICAL' THEN 1 ELSE 0 END) as critical,
  SUM(CASE WHEN severity = 'HIGH' THEN 1 ELSE 0 END) as high,
  AVG(CASE WHEN status = 'OPEN' THEN age_in_days END) as avg_days_open,
  SUM(CASE WHEN status = 'FIXED' AND age_in_days <= 7 THEN 1 ELSE 0 END) 
    / NULLIF(SUM(CASE WHEN status = 'FIXED' THEN 1 ELSE 0 END), 0) 
    as fixed_within_sla
  
FROM security_findings
WHERE scan_date >= CURRENT_DATE - 30
GROUP BY project_name
ORDER BY critical DESC, high DESC;</code></pre>

      <h2>Metrics-Driven Improvements</h2>
      <table class="min-w-full border">
        <thead><tr><th class="border px-4 py-2">Metric</th><th class="border px-4 py-2">Before</th><th class="border px-4 py-2">After</th></tr></thead>
        <tbody>
          <tr><td class="border px-4 py-2">Mean Time to Remediate</td><td class="border px-4 py-2">42 days</td><td class="border px-4 py-2">6.3 days</td></tr>
          <tr><td class="border px-4 py-2">Critical Vulnerabilities</td><td class="border px-4 py-2">18.7 per project</td><td class="border px-4 py-2">2.1 per project</td></tr>
          <tr><td class="border px-4 py-2">Security Test Coverage</td><td class="border px-4 py-2">47%</td><td class="border px-4 py-2">94%</td></tr>
        </tbody>
      </table>
    `
  }
];

const BlogPost = () => {
  const { postId } = useParams();
  const post = blogPosts.find(p => p.id === Number(postId));

  // Activate syntax highlighting when content loads
  useEffect(() => {
    Prism.highlightAll();
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">404 - Post Not Found</h1>
          <Link to="/blog" className="text-blue-600 hover:underline">
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{post.title} | HunterFortress Security</title>
        <meta name="description" content={post.excerpt} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={post.image} />
      </Helmet>

      {/* Navigation Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
            <img 
              src="/HunterFortress_Logo_Shield_Crosshairs_Optimized.png" 
              alt="HunterFortress Logo" 
              className="h-10 mr-3"
              width={40}
              height={40}
            />
            <span className="text-xl font-bold text-blue-900">HunterFortress</span>
          </Link>
          <Link 
            to="/blog" 
            className="text-blue-600 hover:underline flex items-center group"
          >
            <svg 
              className="w-5 h-5 mr-1 group-hover:-translate-x-1 transition-transform" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Blog
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <motion.article
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-xl shadow-md overflow-hidden"
        >
          {/* Featured Image with Lazy Loading */}
          <div className="h-64 md:h-80 bg-gray-100 overflow-hidden">
            <img 
              src={post.image} 
              alt={post.title}
              className="w-full h-full object-cover"
              loading="lazy"
              width={800}
              height={400}
            />
          </div>

          {/* Article Content */}
          <div className="p-6 md:p-8">
            <div className="flex justify-between items-center mb-6">
              <span className="text-sm text-gray-500">{post.date}</span>
              <span className="text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                {post.readTime}
              </span>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {post.title}
            </h1>
            
            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-8">
              {post.tags.map(tag => (
                <Link 
                  key={tag} 
                  to={`/blog?tag=${tag.toLowerCase()}`}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm px-3 py-1 rounded-full transition-colors"
                >
                  #{tag}
                </Link>
              ))}
            </div>
            
            {/* Article Body with Sanitized HTML */}
            <div 
              className="prose prose-blue max-w-none"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }} 
            />

            {/* Author Bio */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex items-center">
                <img 
                  src="/team-security-engineer.jpg" 
                  alt="Security Engineer" 
                  className="w-12 h-12 rounded-full mr-4"
                  width={48}
                  height={48}
                  loading="lazy"
                />
                <div>
                  <h4 className="font-bold">Security Engineering Team</h4>
                  <p className="text-gray-600 text-sm">HunterFortress Security Engineers</p>
                </div>
              </div>
            </div>
          </div>
        </motion.article>

        {/* Newsletter CTA */}
        <NewsletterSubscribe />
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-8 mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center mb-4 md:mb-0">
            <img src="/HunterFortress_Logo_Shield_Crosshairs_Optimized.png" alt="Logo" className="h-10 mr-3" />
            <span className="text-xl font-bold">HunterFortress</span>
          </div>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            <Link to="/" className="hover:text-blue-300 transition font-semibold">Home</Link>
            <Link to="/contact" className="hover:text-blue-300 transition font-semibold">Contact</Link>
            <Link to="/storepal" className="hover:text-blue-300 transition font-semibold">StorePal</Link>
            <Link to="/terms" className="hover:text-blue-300 transition font-semibold">Terms</Link>
            <Link to="/privacy" className="hover:text-blue-300 transition">Privacy</Link>
            <Link to="/blog" className="hover:text-blue-300 transition">Blog</Link>
          </div>
        </div>
        <div className="text-center mt-6 pt-6 border-t border-blue-700">
          <p>&copy; 2025 HunterFortress. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BlogPost;